import { useMutation } from '@tanstack/react-query'
import { api, getErrorMessage } from '@/lib/api'
import { toast } from 'sonner'

/**
 * Message content type for chat messages
 */
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool'
  content?: string | null
  function_call?: {
    name: string
    arguments: string
  }
  tool_calls?: Array<{
    id: string
    type: string
    function: {
      name: string
      arguments: string
    }
  }>
  tool_call_id?: string
  refusal?: Record<string, string>
}

/**
 * Request payload for trying an assistant
 */
export interface TryAssistantRequest {
  assistant_id: number
  test_conversation_id?: string | null
  messages: ChatMessage[]
  caller_number?: string | null
}

/**
 * Response from the try assistant endpoint
 */
export interface TryAssistantResponse {
  test_conversation_id: string
  messages: ChatMessage[]
  caller_number: string
}

/**
 * Query keys for try assistant
 */
export const tryAssistantKeys = {
  all: ['try-assistant'] as const,
  session: (assistantId: number, conversationId?: string) =>
    [...tryAssistantKeys.all, assistantId, conversationId] as const,
}

/**
 * Hook for trying an assistant with chat messages
 */
export function useTryAssistant() {
  return useMutation({
    mutationFn: async (request: TryAssistantRequest): Promise<TryAssistantResponse> => {
      const response = await api.post<TryAssistantResponse>('/assistants/try', request)
      return response.data
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Failed to send message', {
        description: message,
      })
    },
  })
}

/**
 * Hook for starting a new conversation
 */
export function useStartConversation() {
  return useMutation({
    mutationFn: async ({
      assistantId,
      callerNumber,
    }: {
      assistantId: number
      callerNumber?: string
    }): Promise<TryAssistantResponse> => {
      const response = await api.post<TryAssistantResponse>('/assistants/try', {
        assistant_id: assistantId,
        messages: [],
        caller_number: callerNumber,
      })
      return response.data
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error('Failed to start conversation', {
        description: message,
      })
    },
  })
}

export default useTryAssistant
